import { createRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AnimatePresence } from "motion/react";
import { Button, Card, ConfirmDialog, Screen, Skeleton, type Tab } from "../../shared/ui/index.js";
import { Pane } from "../../shared/motion.js";
import { plural } from "../../shared/format.js";
import { Shell } from "../../app/Shell.js";
import { requireAuth } from "../../app/guards.js";
import { rootRoute } from "../../app/root-route.js";
import { useDeleteWedding, useWedding } from "./weddings.api.js";
import { CoupleForm } from "./CoupleForm.js";
import { CouplePhotosPanel } from "./CouplePhotosPanel.js";
import { EventsPanel } from "./EventsPanel.js";
import { TeamPanel } from "./TeamPanel.js";

export const appWeddingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/weddings/$id",
  beforeLoad: requireAuth(),
  component: () => (
    <Shell>
      <WeddingScreen />
    </Shell>
  ),
});

const TABS: Tab[] = [
  { id: "couple", label: "Le couple" },
  { id: "photos", label: "Photos" },
  { id: "events", label: "Événements" },
  { id: "team", label: "Équipe" },
  { id: "settings", label: "Réglages" },
];

function WeddingScreen() {
  const { id } = appWeddingRoute.useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useWedding(id);
  const del = useDeleteWedding(id);
  const [tab, setTab] = useState("couple");
  const [confirmDel, setConfirmDel] = useState(false);

  if (isLoading || !data) {
    return (
      <Screen title="…">
        <Skeleton count={3} className="h-40" />
      </Screen>
    );
  }

  const w = data.wedding;

  return (
    <Screen
      title={`${w.partner1Name} & ${w.partner2Name}`}
      subtitle={
        <>
          {data.events.length} {plural(data.events.length, "événement")} ·{" "}
          {data.couplePhotos.length} {plural(data.couplePhotos.length, "photo")} du couple
        </>
      }
      tabs={TABS}
      activeTab={tab}
      onTab={setTab}
    >
      <AnimatePresence mode="wait">
        {tab === "couple" && (
          <Pane id="couple">
            <CoupleForm wedding={w} />
          </Pane>
        )}
        {tab === "photos" && (
          <Pane id="photos">
            <CouplePhotosPanel weddingId={id} photos={data.couplePhotos} />
          </Pane>
        )}
        {tab === "events" && (
          <Pane id="events">
            <EventsPanel weddingId={id} events={data.events} />
          </Pane>
        )}
        {tab === "team" && (
          <Pane id="team">
            <TeamPanel weddingId={id} />
          </Pane>
        )}
        {tab === "settings" && (
          <Pane id="settings">
            <Card className="border-rose-200 bg-rose-50/60">
              <h2 className="font-serif text-lg text-rose-900">Supprimer ce mariage</h2>
              <p className="mt-1 text-sm text-rose-700">
                Efface définitivement le mariage, ses événements et tous les médias déposés par
                les invités.
              </p>
              <Button variant="danger" className="mt-4" onClick={() => setConfirmDel(true)}>
                Supprimer
              </Button>
            </Card>
          </Pane>
        )}
      </AnimatePresence>

      {confirmDel && (
        <ConfirmDialog
          title="Supprimer ce mariage ?"
          body={`"${w.partner1Name} & ${w.partner2Name}", ses ${data.events.length} événement(s) et tous les médias seront définitivement supprimés.`}
          confirmLabel="Supprimer définitivement"
          onConfirm={() => del.mutate(undefined, { onSuccess: () => navigate({ to: "/app" }) })}
          onClose={() => setConfirmDel(false)}
        />
      )}
    </Screen>
  );
}
